import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../api/axiosInstance';
import { useAtomValue } from 'jotai';
import { supervisorsListState } from '../jotai/atoms';
import AssociationReportDetailModal from './AssociationReportDetailModal'; // New modal import
import { useAuth } from '../context/AuthContext';

function AssociationReportManagementPage() {
  const { user } = useAuth();
  const supervisors = useAtomValue(supervisorsListState);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('단가'); // Assuming '단가' and '총가' are still relevant
  const [selectedYear, setSelectedYear] = useState('all');
  const [allTasks, setAllTasks] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // New state for filtering

  const [isReportDetailModalOpen, setIsReportDetailModalOpen] = useState(false); // 신고관리 모달 상태
  const [selectedTaskForReport, setSelectedTaskForReport] = useState(null); // 신고관리 대상 공사
  const [isModalReadOnly, setIsModalReadOnly] = useState(false); // 모달 읽기 전용 상태

  const currentYear = new Date().getFullYear();
  const years = ['all', ...Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString())];

  useEffect(() => {
    const currentYearStr = new Date().getFullYear().toString();
    if (activeTab === '단가') {
      setSelectedYear(currentYearStr);
    } else if (activeTab === '총가') {
      setSelectedYear('all');
    }
  }, [activeTab]);

  const fetchAllTasks = useCallback(async (year) => {
    try {
      const tasksUrl = year === 'all' ? '/tasks' : `/tasks?workYear=${year}`;
      const tasksResponse = await axiosInstance.get(tasksUrl);
      const tasks = tasksResponse.data;

      // Fetch all association reports
      const associationReportsResponse = await axiosInstance.get('/association-reports');
      const associationReports = associationReportsResponse.data;

      // Fetch all schedule entries
      const scheduleEntriesResponse = await axiosInstance.get('/schedule-entries');
      const scheduleEntries = scheduleEntriesResponse.data;

      // Map report dates and latest schedule dates to tasks
      const tasksWithAdditionalData = await Promise.all(tasks.map(async task => {
        const report = associationReports.find(ar => ar.taskId === task.id);

        // Find the latest scheduledDate for the current task
        const taskScheduleEntries = scheduleEntries.filter(entry => entry.task && entry.task.id === task.id);
        let latestScheduleDate = null;
        if (taskScheduleEntries.length > 0) {
          // Sort by scheduledDate in descending order and take the first one
          taskScheduleEntries.sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));
          latestScheduleDate = taskScheduleEntries[0].scheduledDate;
        }

        // Fetch supervisor changes date
        let supervisorChangedDate = null;
        if (task.id) { // Only fetch if taskId exists
          const supervisorChangesResponse = await axiosInstance.get(`/association-reports/supervisor-changes/${task.id}`);
          supervisorChangedDate = supervisorChangesResponse.data; // This will be a date string or null
        }

        return {
          ...task,
          reportDate: report ? report.reportDate : null,
          latestScheduleDate: latestScheduleDate,
          supervisorChangedDate: supervisorChangedDate, // Store the date directly
        };
      }));

      setAllTasks(tasksWithAdditionalData);
      return tasksWithAdditionalData; // Return the fetched tasks
    } catch (error) {
      console.error('Error fetching data:', error);
      setAllTasks([]);
      return []; // Return empty array on error
    }
  }, [setAllTasks]);

  useEffect(() => {
    fetchAllTasks(selectedYear);
  }, [selectedYear, fetchAllTasks]);

  const getFilteredTasks = () => {
    return allTasks.filter(task => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const matchesSearch = (
        task.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        task.displayNumber.toLowerCase().includes(lowerCaseSearchTerm) ||
        task.number.toLowerCase().includes(lowerCaseSearchTerm)
      );
      const matchesTab = task.type === activeTab;

      // Determine the task's report status for filtering
      let taskReportStatus = '';
      if (!task.reportDate && !task.latestScheduleDate) {
        taskReportStatus = '미착공';
      } else if (task.reportDate === task.latestScheduleDate) {
        taskReportStatus = '신고완료'; // Not a filter option, but good for internal logic
      } else {
        taskReportStatus = '신고필요';
      }

      // Determine if the task has a replacement
      // Check if supervisorChangedDate is a truthy value (not null, undefined, or empty string)
      const hasReplacement = !!task.supervisorChangedDate;

      // console.log(`Task: ${task.name}, supervisorChangedDate: ${task.supervisorChangedDate}, hasReplacement: ${hasReplacement}, filterStatus: ${filterStatus}`);

      const matchesFilter = filterStatus === 'all' ||
                            (filterStatus === '신고필요' && taskReportStatus === '신고필요') ||
                            (filterStatus === '교체' && hasReplacement) ||
                            (filterStatus === '미착공' && taskReportStatus === '미착공');

      return matchesSearch && matchesTab && matchesFilter;
    }).sort((a, b) => {
      const numA = parseInt(a.displayNumber, 10);
      const numB = parseInt(b.displayNumber, 10);
      return numA - numB;
    });
  };

  

  const handleSaveReportDate = async (taskId, reportDate, managers, assistants) => {
    let associationData;
    let existingReportFound = false; // Flag to check if an existing report is found

    try {
      // First, check if an association report already exists for this taskId
      const response = await axiosInstance.get(`/association-reports/by-task/${taskId}`);
      const existingReports = response.data;

      if (existingReports && existingReports.length > 0) {
        existingReportFound = true;
      }

      if (managers) {
        // Called from schedule click
        associationData = {
          taskId: taskId,
          taskName: allTasks.find(t => t.id === taskId)?.name,
          reportDate: reportDate, // Changed from associationReportDate back to reportDate to match backend
          assignedManagers: managers,
          assignedAssistants: assistants || '',
        };
      } else {
        // Called from manual date input.
        const task = allTasks.find(t => t.id === taskId);
        associationData = {
          taskId: taskId,
          taskName: task?.name,
          reportDate: reportDate, // Changed from associationReportDate back to reportDate to match backend
          assignedManagers: task?.managers || '',
          assignedAssistants: task?.assistants || '',
        };
      }

      if (existingReportFound) {
        // Use PUT to update the existing report
        await axiosInstance.put(`/association-reports/by-task/${taskId}`, associationData);
        Swal.fire(
          '업데이트 완료!',
          '협회신고 정보가 성공적으로 업데이트되었습니다.',
          'success'
        );
      } else {
        // Use POST to create a new report
        await axiosInstance.post('/association-reports', associationData);
        Swal.fire(
          '저장 완료!',
          '협회신고 정보가 성공적으로 저장되었습니다.',
          'success'
        );
      }
      fetchAllTasks(selectedYear); // Refresh data
    } catch (error) {
      console.error('협회신고 정보 처리 중 오류 발생:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '협회신고 정보 처리 중 오류가 발생했습니다.',
      });
    }
  };

  const filteredTasks = getFilteredTasks();
  const totalCount = filteredTasks.length;
  const reportNeededCount = filteredTasks.filter(task => {
    const taskReportStatus = (!task.reportDate && !task.latestScheduleDate) ? '미착공' : (task.reportDate === task.latestScheduleDate ? '신고완료' : '신고필요');
    return taskReportStatus === '신고필요';
  }).length;
  const replacementCount = filteredTasks.filter(task => !!task.supervisorChangedDate).length;
  const notStartedCount = filteredTasks.filter(task => {
    const taskReportStatus = (!task.reportDate && !task.latestScheduleDate) ? '미착공' : (task.reportDate === task.latestScheduleDate ? '신고완료' : '신고필요');
    return taskReportStatus === '미착공';
  }).length;
  const reportCompletedCount = filteredTasks.filter(task => {
    const taskReportStatus = (!task.reportDate && !task.latestScheduleDate) ? '미착공' : (task.reportDate === task.latestScheduleDate ? '신고완료' : '신고필요');
    return taskReportStatus === '신고완료';
  }).length;

  return (
    <div className="page-container">
      <br/><br/>
      <h2>협회신고관리</h2>
      <br/><br/>

      <div className="assign-task-section">
        <div className="filter-controls">
          <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear} className="year-select">
            <option value="all">전체공사목록</option>
            {years.filter(year => year !== 'all').map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
          <select onChange={(e) => setFilterStatus(e.target.value)} value={filterStatus} className="filter-select">
            <option value="all">전체 상태</option>
            <option value="신고필요">신고필요</option>
            <option value="교체">교체</option>
            <option value="미착공">미착공</option>
          </select>
          <input
            type="text"
            placeholder="공사명, 번호, 공사번호로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input full-width-input"
          />
        </div>
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === '단가' ? 'active' : ''}`}
            onClick={() => setActiveTab('단가')}
          >
            단가
          </button>
          <button
            className={`tab-button ${activeTab === '총가' ? 'active' : ''}`}
            onClick={() => setActiveTab('총가')}
          >
            총가
          </button>
        </div>
        
        <div className="task-list-container" style={{ minHeight: '560px', marginBottom: '50px' }}>
          {filteredTasks.length > 0 ? (
            <table className="main-table assign-task-table">
              <thead>
                <tr>
                  <th style={{ width: '6%', textAlign: 'center' }}>계약연도</th>
                  <th style={{ width: '1%', textAlign: 'center' }}>번호</th>
                  <th style={{ width: '15%', textAlign: 'center' }}>공사명</th>
                  <th style={{ width: '10%', textAlign: 'center' }}>공사번호</th>
                  <th style={{ textAlign: 'center' }}>협회신고날짜</th>
                  <th style={{ textAlign: 'center' }}>최근 공사일정</th>
                  <th style={{ textAlign: 'center' }}>신고상태</th>
                  <th style={{ textAlign: 'center' }}>교체</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>책임감리</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>보조감리</th>
                  {user && user.role === 'ADMIN' && <th style={{ textAlign: 'center' }}>관리</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => {
                  const isCompleted = task.period === '준공';
                  const taskReportStatus = !task.reportDate && !task.latestScheduleDate ? '미착공' : (task.reportDate === task.latestScheduleDate ? '신고완료' : '신고필요');
                  const hasReplacement = !!task.supervisorChangedDate;

                  const getStatusStyle = (status, hasReplacement) => {
                    if (status === '신고필요' && hasReplacement) {
                      return { backgroundColor: 'red', color: 'white', textAlign: 'center' };
                    }
                    switch (status) {
                      case '신고필요':
                        return { backgroundColor: 'yellow', textAlign: 'center' };
                      case '신고완료':
                        return { backgroundColor: 'lightgreen', textAlign: 'center' };
                      default:
                        return { textAlign: 'center' };
                    }
                  };

                  return (
                  <tr
                    key={task.id}
                    onClick={() => {
                      
                        setSelectedTaskForReport(task);
                        setIsReportDetailModalOpen(true);
                        setIsModalReadOnly(true); // 읽기 전용 모드
                      
                    }}
                    className={`${isCompleted ? 'completed-task' : ''}`} // Removed selected class
                    // style={{ cursor: isCompleted ? 'not-allowed' : 'pointer' }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ width: '5%', textAlign: 'center' }}>{new Date(task.contractDate).getFullYear()}</td>
                    <td style={{ width: '5%', textAlign: 'center' }}>{task.displayNumber}</td>
                    <td style={{ width: '15%', textAlign: 'center' }}>{task.name}{isCompleted ? ' (준공)' : ''}</td>
                    <td style={{ width: '5%', textAlign: 'center' }}>{task.number}</td>
                    <td style={{ textAlign: 'center' }}>{task.reportDate}</td>
                    <td style={{ textAlign: 'center' }}>{task.latestScheduleDate}</td>
                    <td style={getStatusStyle(taskReportStatus, hasReplacement)}>
                      {taskReportStatus}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {task.supervisorChangedDate ? (
                        <span style={{ color: 'red' }}>
                          {new Date(new Date(task.supervisorChangedDate).setDate(new Date(task.supervisorChangedDate).getDate() + 29)).toISOString().slice(0, 10)}
                        </span>
                      ) : ''}
                    </td>
                    <td style={{ textAlign: 'center' }}>{task.managers}</td>
                    <td style={{ textAlign: 'center' }}>{task.assistants}</td>
                    {user && user.role === 'ADMIN' && (
                      <td>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row selection when button is clicked
                            setSelectedTaskForReport(task);
                            setIsReportDetailModalOpen(true);
                            setIsModalReadOnly(false); // 편집 가능 모드
                          }}
                          className="action-button2"
                        >
                          신고관리
                        </button>
                      </td>
                    )}
                  </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <p>표시할 공사가 없습니다.</p>
          )}
        </div>

        
      </div>

      {selectedTaskForReport && (
        <AssociationReportDetailModal
          isOpen={isReportDetailModalOpen}
          onClose={() => setIsReportDetailModalOpen(false)}
          task={selectedTaskForReport}
          onSaveReportDate={handleSaveReportDate}
          onReportChange={async () => {
            const updatedTasks = await fetchAllTasks(selectedYear);
            const updatedTask = updatedTasks.find(t => t.id === selectedTaskForReport.id);
            if (updatedTask) {
              setSelectedTaskForReport(updatedTask);
            }
          }}
          isReadOnly={isModalReadOnly} // isReadOnly prop 전달
        />
      )}

      <div className="summary-footer">
        <span>Total: {totalCount}건</span>
        <span>신고완료: {reportCompletedCount}건</span>
        <span>신고필요: {reportNeededCount}건</span>
        <span>교체: {replacementCount}건</span>
        <span>미착공: {notStartedCount}건</span>
      </div>
    </div>
  );
}

export default AssociationReportManagementPage;